import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accountLinkIssueSchema,
  userCreateResultSchema,
  userCreateSchema,
  userPageSchema,
  userSchema,
  userUpdateSchema,
  type UserCreate,
  type UserUpdate,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';

/**
 * Data access for the Admin Users area — the existing endpoints only (all `users.manage`):
 *   GET   /api/users[?cursor=&limit=]   portal users, oldest first (cursor pagination)
 *   POST  /api/users                    create: email and role; emails an invitation link
 *   PATCH /api/users/:userId            change role and/or status; signs that user out everywhere
 *   POST  /api/users/:userId/invitation      resend the link of a user with no password yet
 *   POST  /api/users/:userId/password-reset  remove a user's password and email a reset link
 * The API generates the Bekebi User ID and protects the last active Admin. The Admin never sets
 * or sees another user's password (DECISIONS.md A-AC1). A link returned because its email did not
 * go out is shown once and never stored in the browser.
 */
export const userKeys = { all: ['users'] as const };

export function useUsers() {
  return useInfiniteQuery({
    queryKey: userKeys.all,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/users?${params}`, { schema: userPageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UserCreate) =>
      apiRequest('/users', {
        method: 'POST',
        body: userCreateSchema.parse(input),
        schema: userCreateResultSchema,
      }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...patch }: UserUpdate & { userId: string }) =>
      apiRequest(`/users/${userId}`, {
        method: 'PATCH',
        body: userUpdateSchema.parse(patch),
        schema: userSchema,
      }),
    // Re-read the list from the API: it is the authority on every user's role and status.
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Resend (`invitation`) or reset (`password-reset`): a new one-time link for one user. */
export function useSendAccountLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, kind }: { userId: string; kind: 'invitation' | 'password-reset' }) =>
      apiRequest(`/users/${userId}/${kind}`, { method: 'POST', schema: accountLinkIssueSchema }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}
